import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, Send, X, Bot, User, Copy, Check, Trash2 } from 'lucide-react';
import { AuthContext } from '../store/ContextStore';
import { useChatHistory } from '../store/ChatHistoryContext';
import ChatBotService from '../services/chatBotService';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ChatBotProps {
  entity: string;
}

const LOADING_STAGES = [
  'Searching sports data...',
  'Analyzing your question...',
  'Generating response...',
];

function formatTime(date: Date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Reveals bot text word-by-word for a polished answer load feel */
const AnimatedAnswer: React.FC<{ text: string; animate: boolean; onComplete?: () => void }> = ({
  text,
  animate,
  onComplete,
}) => {
  const [displayedWords, setDisplayedWords] = useState(animate ? 0 : Infinity);
  const words = text.split(/(\s+)/);
  const wordCount = words.filter((w) => w.trim()).length;

  useEffect(() => {
    if (!animate) {
      setDisplayedWords(Infinity);
      return;
    }

    setDisplayedWords(0);
    let current = 0;
    const interval = setInterval(() => {
      current += 1;
      setDisplayedWords(current);
      if (current >= wordCount) {
        clearInterval(interval);
        onComplete?.();
      }
    }, 35);

    return () => clearInterval(interval);
  }, [text, animate, wordCount, onComplete]);

  const visibleText = animate
    ? words.reduce((acc, token, i) => {
        const wordIndex = words.slice(0, i + 1).filter((w) => w.trim()).length;
        return wordIndex <= displayedWords ? acc + token : acc;
      }, '')
    : text;

  const isTyping = animate && displayedWords < wordCount;

  return (
    <div className="relative">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p className="m-0 text-sm leading-relaxed whitespace-pre-wrap break-words">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="my-1.5 list-disc pl-5 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-1.5 list-decimal pl-5 space-y-1">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="text-sm whitespace-pre-wrap break-words [&>p]:m-0">{children}</li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">{children}</strong>
          ),
          a: ({ children, href }) => (
            <a className="text-teal-600 underline hover:text-teal-700" href={href} target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="text-xs bg-gray-200/80 rounded px-1.5 py-0.5 font-mono">{children}</code>
          ),
        }}
      >
        {visibleText}
      </ReactMarkdown>
      {isTyping && (
        <span className="inline-block w-0.5 h-4 bg-teal-500 ml-0.5 align-middle animate-pulse" />
      )}
    </div>
  );
};

const LoadingIndicator: React.FC<{ teamColor: string }> = ({ teamColor }) => {
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    const timers = LOADING_STAGES.map((_, i) =>
      setTimeout(() => setStageIndex(i), i * 1800)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="flex justify-start animate-chat-fade-in">
      <div className="flex gap-2.5 max-w-[85%]">
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
          style={{ backgroundColor: `${teamColor}18`, color: teamColor }}
        >
          <Bot className="w-4 h-4" />
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm min-w-[200px]">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full animate-typing-dot"
                  style={{ backgroundColor: teamColor, animationDelay: `${i * 200}ms` }}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-gray-500 transition-all duration-500">
              {LOADING_STAGES[stageIndex]}
            </span>
          </div>
          <div
            className="h-1 rounded-full overflow-hidden bg-gray-100"
          >
            <div
              className="h-full rounded-full transition-all duration-[1800ms] ease-out"
              style={{
                backgroundColor: teamColor,
                width: `${((stageIndex + 1) / LOADING_STAGES.length) * 100}%`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatBot: React.FC<ChatBotProps> = ({ entity: entityProp }) => {
  const authContext = useContext(AuthContext);
  const { team, selectedSport: contextSport, user } = authContext || {};

  const selectedSport = contextSport || 'MFB';
  const teamColor = team?.colorCode || '#00B2B2';

  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [entity, setEntity] = useState<string>('Player');
  const [animatingMessageId, setAnimatingMessageId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { getCurrentMessages, addMessage, initializeChat, resetChat } = useChatHistory();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatBotService = useRef(new ChatBotService());

  const currentMessages = getCurrentMessages();

  useEffect(() => {
    setEntity(entityProp);
  }, [entityProp]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages, isOpen, isLoading, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      initializeChat();
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, initializeChat]);

  const sendQuestion = async (question: string) => {
    if (!question.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: question.trim(),
      sender: 'user',
      timestamp: new Date(),
    };
    addMessage(userMessage);
    setInputText('');
    setIsLoading(true);

    try {
      const response = await chatBotService.current.askQuestion({
        question: question.trim(),
        top_k: 3,
        sport_code: selectedSport,
        entity,
        username: user?.username || '',
      });

      const botMessageId = (Date.now() + 1).toString();
      const botMessage: Message = {
        id: botMessageId,
        text: response.answer,
        sender: 'bot',
        timestamp: new Date(),
      };
      addMessage(botMessage);
      setAnimatingMessageId(botMessageId);
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage({
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error processing your request. Please try again in a moment.',
        sender: 'bot',
        timestamp: new Date(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = () => sendQuestion(inputText);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopy = async (id: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleClearChat = () => {
    resetChat();
    setAnimatingMessageId(null);
  };

  return (
    <>
      {/* Floating launcher */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          aria-label="Open Ask Athlyte Bot"
        >
          <span
            className="absolute inset-0 rounded-full animate-pulse-ring"
            style={{ backgroundColor: teamColor }}
          />
          <span
            className="relative flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl"
            style={{
              background: `linear-gradient(135deg, ${teamColor} 0%, ${teamColor}cc 100%)`,
            }}
          >
            <Sparkles className="w-6 h-6 text-white" />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 w-[400px] h-[560px] z-50 flex flex-col rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden animate-chat-slide-up"
          style={{ background: 'linear-gradient(180deg, #fafbfc 0%, #ffffff 40%)' }}
        >
          {/* Header */}
          <div
            className="relative px-4 py-3.5 flex items-center justify-between"
            style={{ background: `linear-gradient(135deg, ${teamColor} 0%, ${teamColor}dd 100%)` }}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm leading-tight">Ask Athlyte Bot</h3>
                <p className="text-white/75 text-xs">Sports analytics assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleClearChat}
                className="p-2 text-white/80 hover:text-white hover:bg-white/15 rounded-lg transition-colors"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/15 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 custom-scrollbar">
            {currentMessages.map((message, index) => {
              const isUser = message.sender === 'user';
              const shouldAnimate = message.id === animatingMessageId;

              return (
                <div
                  key={message.id}
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-chat-fade-in`}
                  style={{ animationDelay: `${Math.min(index * 50, 200)}ms`, opacity: 0 }}
                >
                  <div className={`flex gap-2.5 max-w-[88%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Avatar */}
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                        isUser ? 'text-white' : ''
                      }`}
                      style={
                        isUser
                          ? { backgroundColor: teamColor }
                          : { backgroundColor: `${teamColor}18`, color: teamColor }
                      }
                    >
                      {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                    </div>

                    {/* Bubble */}
                    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                      <div
                        className={`group relative rounded-2xl px-4 py-2.5 shadow-sm ${
                          isUser
                            ? 'rounded-tr-sm text-white'
                            : 'rounded-tl-sm bg-white border border-gray-100 text-gray-800'
                        }`}
                        style={isUser ? { backgroundColor: teamColor } : undefined}
                      >
                        {isUser ? (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.text}</p>
                        ) : (
                          <AnimatedAnswer
                            text={message.text}
                            animate={shouldAnimate}
                            onComplete={() => setAnimatingMessageId(null)}
                          />
                        )}

                        {/* Copy button for bot messages */}
                        {!isUser && !shouldAnimate && (
                          <button
                            onClick={() => handleCopy(message.id, message.text)}
                            className="absolute -bottom-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50"
                            title="Copy answer"
                          >
                            {copiedId === message.id ? (
                              <Check className="w-3 h-3 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Timestamp */}
                      <span className="text-[10px] text-gray-400 mt-1 px-1">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {isLoading && <LoadingIndicator teamColor={teamColor} />}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white/80 backdrop-blur-sm">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask about stats, players, teams..."
                  rows={1}
                  className="w-full px-4 py-2.5 pr-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm resize-none max-h-24 leading-relaxed bg-gray-50/50 focus:bg-white transition-colors"
                  style={{ ['--tw-ring-color' as string]: `${teamColor}40` }}
                  disabled={isLoading}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${Math.min(target.scrollHeight, 96)}px`;
                  }}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isLoading}
                className="flex-shrink-0 p-2.5 rounded-xl text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 active:scale-95 transition-all shadow-sm"
                style={{ backgroundColor: teamColor }}
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              Press Enter to send · Shift+Enter for new line
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
